/**
 * Admin API client for GenAI usage dashboard + match-level toggles.
 * Talks to dugout-be (BASE_URL). Auth: admin Bearer token (sessionStorage).
 */
import { BASE_URL } from '../components/AppConfig';

function getAuthHeaders() {
  const token = sessionStorage.getItem('sessionToken');
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

async function handle(res) {
  if (!res.ok) {
    let msg = `Request failed: ${res.status}`;
    try { const body = await res.json(); msg = body.message || body.error || msg; } catch {}
    throw new Error(msg);
  }
  const body = await res.json().catch(() => ({}));
  // Backend envelope: { statusCode, message, data: { ... } }
  return body.data ?? body;
}

/** GET /admin/genai/usage?from=&to=&group_by=usecase|day|usecase_day */
export async function fetchUsage({ from, to, groupBy = 'usecase' }) {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  if (groupBy) qs.set('group_by', groupBy);
  const res = await fetch(`${BASE_URL}/admin/genai/usage?${qs.toString()}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });
  return handle(res);
}

/**
 * GET /admin/genai/usage/by-service?from=&to=&group_by=service|service_day
 *
 * Full per-(service, model, provider) breakdown from genai_usage_log
 * (dugout-be migration 145). Includes dollar cost per row + a
 * pre-aggregated by_service list with model breakdown inline, so one
 * response drives both "cost per (service × model)" and "total per
 * service" columns in the same table.
 *
 * Response shape:
 *   {
 *     from, to, group_by,
 *     rates_used: { [model]: { in, out } },
 *     rows:       [{ service, model, provider, calls, input_tokens,
 *                    output_tokens, total_tokens, errors, cost_usd,
 *                    rate_matched, first_ts, last_ts }],
 *     by_service: [{ service, calls, input_tokens, output_tokens,
 *                    total_tokens, errors, cost_usd,
 *                    by_model: [{ model, provider, calls, cost_usd, ...}] }],
 *     totals:     { calls, input_tokens, output_tokens, total_tokens,
 *                   errors, cost_usd }
 *   }
 */
export async function fetchUsageByService({ from, to, groupBy = 'service' }) {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  if (groupBy) qs.set('group_by', groupBy);
  const res = await fetch(`${BASE_URL}/admin/genai/usage/by-service?${qs.toString()}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });
  return handle(res);
}

/** GET /competitions?limit=&offset= — reused list (already exists on dugout-be) */
export async function fetchCompetitions({ limit = 200, offset = 0 } = {}) {
  const res = await fetch(`${BASE_URL}/competitions?limit=${limit}&offset=${offset}`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });
  return handle(res);
}

/** GET /admin/genai/tournaments/:cid/matches */
export async function fetchTournamentMatches(cid) {
  const res = await fetch(`${BASE_URL}/admin/genai/tournaments/${cid}/matches`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });
  return handle(res);
}

/** GET /admin/genai/competitions/:cid/config */
export async function fetchCompetitionConfig(cid) {
  const res = await fetch(`${BASE_URL}/admin/genai/competitions/${cid}/config`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });
  return handle(res);
}

/** PATCH /admin/genai/competitions/:cid/config */
export async function patchCompetitionConfig(cid, patch) {
  const res = await fetch(`${BASE_URL}/admin/genai/competitions/${cid}/config`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return handle(res);
}

/** GET /admin/genai/matches/:matchId/config */
export async function fetchMatchConfig(matchId) {
  const res = await fetch(`${BASE_URL}/admin/genai/matches/${matchId}/config`, {
    credentials: 'include',
    headers: getAuthHeaders(),
  });
  return handle(res);
}

/** PATCH /admin/genai/matches/:matchId/config */
export async function patchMatchConfig(matchId, patch) {
  const res = await fetch(`${BASE_URL}/admin/genai/matches/${matchId}/config`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return handle(res);
}

export default {
  fetchUsage,
  fetchCompetitions,
  fetchTournamentMatches,
  fetchMatchConfig,
  patchMatchConfig,
  fetchCompetitionConfig,
  patchCompetitionConfig,
};
